// ============================================================
// NEW ESTABLISHMENT FORM - With Automatic Geocoding
// File: src/components/NewEstablishmentForm.js
// Creates new establishments and automatically geocodes them
// ============================================================

import React, { useState, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { supabase } from '../supabaseClient'
import { geocodeAddress } from '../utils/geocoding'
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLog'
import { Input, Select, Button, Card } from './UI'
import { useTheme } from '../ThemeContext'
import { useOrganization } from '../hooks/useOrganization'
import { useAgents } from '../hooks/useAgents'

export default function NewEstablishmentForm({ onClose, onSuccess, establishmentType = 'mxb' }) {
  const { colors, spacing, typography, radius, brand } = useTheme()
  const { organization } = useOrganization()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const idLabel = organization?.settings?.identifier_label || 'MXB Number'
  const { agents } = useAgents({ activeOnly: true, role: ['inspector', 'admin'] })
  const [formData, setFormData] = useState({
    mxb_number: '',
    trade_name: '',
    address: '',
    city: '',
    state: 'NC',
    zip: '',
    assigned_inspector_id: ''
  })

  const [cities, setCities] = useState([])
  const [loading, setLoading] = useState(false)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [error, setError] = useState(null)
  const [geocodeWarning, setGeocodeWarning] = useState(null)
  const [suggestedAgent, setSuggestedAgent] = useState(null)

  // Ref to prevent double-submission (updates synchronously unlike state)
  const isSubmittingRef = useRef(false)

  // Fetch cities
  useEffect(() => {
    // Don't fetch until organization is loaded (prevents fetching all data)
    if (!organization) return

    // Clear state when organization changes (prevents flash of old data)
    setCities([])
    setSuggestedAgent(null)
    fetchCities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization])

  async function fetchCities() {
    try {
      let query = supabase
        .from('establishments')
        .select('city')
        .eq('active', true)

      // Filter by organization if provided (important for super_admins)
      if (organization?.id) {
        query = query.eq('organization_id', organization.id)
      }

      const { data, error } = await query

      if (error) throw error

      // Get unique cities and sort them
      const uniqueCities = [...new Set(data.map(est => est.city).filter(Boolean))].sort()
      setCities(uniqueCities)
    } catch (err) {
      console.error('Error fetching cities:', err)
    }
  }

  async function findSuggestedAgent(zip) {
    if (!zip || zip.length !== 5) {
      setSuggestedAgent(null)
      return
    }

    try {
      // Get all establishments in this zip code with their assigned agents
      let query = supabase
        .from('establishments')
        .select('assigned_inspector_id, users!establishments_assigned_inspector_id_fkey(first_name, last_name)')
        .eq('zip', zip)
        .eq('active', true)
        .not('assigned_inspector_id', 'is', null)

      // Filter by organization if provided (important for super_admins)
      if (organization?.id) {
        query = query.eq('organization_id', organization.id)
      }

      const { data, error } = await query

      if (error) throw error

      // Count establishments per agent
      const agentCounts = {}
      data.forEach(est => {
        const agentId = est.assigned_inspector_id
        if (agentId) {
          if (!agentCounts[agentId]) {
            agentCounts[agentId] = {
              id: agentId,
              count: 0,
              name: `${est.users.first_name} ${est.users.last_name}`
            }
          }
          agentCounts[agentId].count++
        }
      })

      // Find agent with most establishments
      const agentList = Object.values(agentCounts)
      if (agentList.length > 0) {
        const topAgent = agentList.reduce((max, agent) =>
          agent.count > max.count ? agent : max
        )
        setSuggestedAgent(topAgent)
      } else {
        setSuggestedAgent(null)
      }
    } catch (err) {
      console.error('Error finding suggested agent:', err)
      setSuggestedAgent(null)
    }
  }

  // Find suggested agent when zip code changes
  useEffect(() => {
    findSuggestedAgent(formData.zip)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.zip])

  function handleChange(e) {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError(null)
    setGeocodeWarning(null)
  }

  function applySuggestion() {
    if (suggestedAgent) {
      setFormData(prev => ({
        ...prev,
        assigned_inspector_id: suggestedAgent.id
      }))
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()

    // Prevent double-submission using ref (synchronous check)
    if (isSubmittingRef.current) {
      console.log('Submission already in progress, ignoring duplicate click')
      return
    }
    isSubmittingRef.current = true

    setLoading(true)
    setError(null)
    setGeocodeWarning(null)
    setIsGeocoding(true)

    try {
      // Validate required fields
      if (!formData.mxb_number || !formData.trade_name || !formData.address || 
          !formData.city || !formData.state || !formData.zip) {
        throw new Error('Please fill in all required fields')
      }

      // Check if MXB number already exists
      const { data: existingMxb } = await supabase
        .from('establishments')
        .select('id, trade_name')
        .eq('mxb_number', formData.mxb_number.trim())
        .eq('active', true)
        .limit(1)

      if (existingMxb && existingMxb.length > 0) {
        throw new Error(`An establishment with this ${establishmentType === 'mxb' ? 'MXB number' : 'File number'} already exists (${existingMxb[0].trade_name})`)
      }

      // Also check for duplicate trade name + address combination
      const { data: existingNameAddr } = await supabase
        .from('establishments')
        .select('id, mxb_number')
        .ilike('trade_name', formData.trade_name.trim())
        .ilike('address', formData.address.trim())
        .eq('active', true)
        .limit(1)

      if (existingNameAddr && existingNameAddr.length > 0) {
        throw new Error(`An establishment with this name and address already exists (${existingNameAddr[0].mxb_number})`)
      }

      // STEP 1: Geocode the address
      console.log('Geocoding address...')
      const geocodeResult = await geocodeAddress(
        formData.address,
        formData.city,
        formData.state,
        formData.zip
      )

      let latitude = null
      let longitude = null

      if (geocodeResult.success) {
        latitude = geocodeResult.latitude
        longitude = geocodeResult.longitude
        console.log('✅ Geocoded successfully:', latitude, longitude)
      } else {
        console.warn('⚠️ Geocoding failed:', geocodeResult.error)
        setGeocodeWarning(geocodeResult.error)
        // Continue anyway - coordinates are optional
      }

      setIsGeocoding(false)

      // STEP 2: Insert establishment with coordinates
      const { data, error: insertError } = await supabase
        .from('establishments')
        .insert([
          {
            mxb_number: formData.mxb_number.trim(),
            trade_name: formData.trade_name.trim(),
            address: formData.address.trim(),
            city: formData.city.trim(),
            state: formData.state,
            zip: formData.zip.trim(),
            assigned_inspector_id: formData.assigned_inspector_id || null,
            organization_id: organization?.id,
            establishment_type: establishmentType,
            active: true,
            latitude: latitude,
            longitude: longitude
          }
        ])
        .select()

      if (insertError) throw insertError

      console.log('✅ Establishment created:', data[0])

      await logAuditEvent(AUDIT_ACTIONS.ESTABLISHMENT_CREATED, {
        establishment_id: data[0].id,
        mxb_number: data[0].mxb_number,
        trade_name: data[0].trade_name,
        address: data[0].address,
        city: data[0].city,
        establishment_type: data[0].establishment_type
      })

      // Success!
      if (geocodeResult.success) {
        toast.success('Establishment created and will appear on the map!')
      } else {
        toast.success('Establishment created! Note: Location could not be found on map.', { duration: 5000 })
      }

      // Reset form
      setFormData({
        mxb_number: '',
        trade_name: '',
        address: '',
        city: '',
        state: 'NC',
        zip: '',
        assigned_inspector_id: ''
      })

      onSuccess()
    } catch (err) {
      console.error('Error creating establishment:', err)
      setError(err.message)
    } finally {
      setLoading(false)
      setIsGeocoding(false)
      isSubmittingRef.current = false
    }
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? 0 : spacing.md
    }}>
      <Card style={{
        maxWidth: isMobile ? '100%' : '600px',
        width: '100%',
        height: isMobile ? '100dvh' : 'auto',
        maxHeight: isMobile ? '100dvh' : '90vh',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: isMobile ? 0 : undefined
      }} padding="lg">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.xl
        }}>
          <h2 style={{
            margin: 0,
            fontSize: typography.fontSizes['2xl'],
            fontWeight: typography.fontWeights.semibold,
            color: colors.text.primary
          }}>
            New {establishmentType === 'mxb' ? 'Mixed Beverage' : 'Beer/Wine'} Establishment
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: typography.fontSizes['3xl'],
              cursor: 'pointer',
              color: colors.text.tertiary,
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{
              padding: spacing.md,
              backgroundColor: '#fee2e2',
              border: `1px solid ${brand.danger}`,
              borderRadius: radius.md,
              color: brand.danger,
              marginBottom: spacing.lg,
              fontSize: typography.fontSizes.sm
            }}>
              {error}
            </div>
          )}

          {geocodeWarning && (
            <div style={{
              padding: spacing.md,
              backgroundColor: '#fef3c7',
              border: `1px solid ${brand.warning}`,
              borderRadius: radius.md,
              color: '#92400e',
              marginBottom: spacing.lg,
              fontSize: typography.fontSizes.sm
            }}>
              ⚠️ {geocodeWarning}
              <br />
              <small>Establishment will be created but won't appear on the map.</small>
            </div>
          )}

          <div style={{ marginBottom: spacing.lg }}>
            <Input
              label={idLabel}
              name="mxb_number"
              value={formData.mxb_number}
              onChange={handleChange}
              placeholder={establishmentType === 'mxb' ? 'MXB-001' : 'BW-001'}
              required
            />
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <Input
              label="Trade Name"
              name="trade_name"
              value={formData.trade_name}
              onChange={handleChange}
              placeholder="Joe's Bar & Grill"
              required
            />
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <Input
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Main Street"
              required
              helperText="Will be automatically located on the map"
            />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: spacing.sm,
            marginBottom: spacing.lg
          }}>
            <Select
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              options={[
                { label: 'Select City', value: '' },
                ...cities.map(city => ({ label: city, value: city }))
              ]}
            />

            <div>
              <label style={{
                display: 'block',
                fontSize: typography.fontSizes.sm,
                fontWeight: typography.fontWeights.medium,
                color: colors.text.secondary,
                marginBottom: spacing.sm
              }}>
                State
              </label>
              <div style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                border: `2px solid ${colors.input.border}`,
                borderRadius: radius.md,
                fontSize: typography.fontSizes.base,
                backgroundColor: colors.bg.tertiary,
                color: colors.text.tertiary
              }}>
                NC
              </div>
            </div>

            <Input
              label="Zip"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              placeholder="27601"
              required
              maxLength="5"
              pattern="[0-9]{5}"
            />
          </div>

          <div style={{ marginBottom: spacing.xl }}>
            <Select
              label="Assigned Agent (Optional)"
              name="assigned_inspector_id"
              value={formData.assigned_inspector_id}
              onChange={handleChange}
              options={[
                { label: 'Unassigned', value: '' },
                ...agents.map(agent => ({
                  label: `${agent.first_name} ${agent.last_name}`,
                  value: agent.id
                }))
              ]}
            />
            {suggestedAgent && formData.assigned_inspector_id !== suggestedAgent.id && (
              <div style={{
                marginTop: spacing.sm,
                padding: spacing.md,
                backgroundColor: '#eff6ff',
                border: `1px solid ${brand.info}`,
                borderRadius: radius.md,
                fontSize: typography.fontSizes.sm,
                color: '#1e40af',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: spacing.sm
              }}>
                <span>
                  💡 <strong>Suggestion:</strong> {suggestedAgent.name} has {suggestedAgent.count} establishment{suggestedAgent.count !== 1 ? 's' : ''} in {formData.zip}
                </span>
                <Button
                  type="button"
                  onClick={applySuggestion}
                  variant="primary"
                  size="sm"
                  style={{ flexShrink: 0 }}
                >
                  Apply
                </Button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              onClick={onClose}
              disabled={loading || isGeocoding}
              variant="secondary"
              size="lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || isGeocoding}
              loading={loading || isGeocoding}
              variant="primary"
              size="lg"
            >
              {isGeocoding ? '📍 Finding location...' : 'Create Establishment'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
